import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, LinearProgress, TextField, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useSnackbar } from "notistack";
import { uploadToApproval } from "../../hooks/useApprovals";
import { LinkifiedText, errorMessage, formatBytes, primaryButtonSx, secondaryButtonSx } from "./approvalUi";

// What a Video Approval version is: the creator's draft cut (uploaded straight
// to S3) plus the caption they'll post it with. Plugged into the shared
// ApprovalReviewPanel through approvalKinds.js. Limits mirror
// pluginbackendnode/helpers/s3VideoUploads.js — checked here only so a wrong
// file fails instantly instead of after the upload.

const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];
const TYPE_BY_EXTENSION = { mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", m4v: "video/x-m4v" };
// Some OS/browser pairs report an empty file.type — fall back to the extension.
const mimeOf = (file) => file.type || TYPE_BY_EXTENSION[file.name.split(".").pop()?.toLowerCase()] || "";
const MAX_BYTES = 1024 * 1024 * 1024;
const CAPTION_MAX = 2200;
// S3 deletes every approval video this long after upload — the
// "video-approvals-expire-90-days" lifecycle rule on the bucket. Keep in sync.
const RETENTION_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

// When this file stops existing. A caption-only version reuses an earlier cut,
// so the clock starts at the FIRST version that carried this file.
const expiresOn = (video, versions = []) => {
  const uploads = versions.filter((v) => v.video?.url === video.url).map((v) => new Date(v.createdOn).getTime());
  return uploads.length ? new Date(Math.min(...uploads) + RETENTION_DAYS * DAY_MS) : null;
};
const isExpired = (video, versions) => {
  const until = expiresOn(video, versions);
  return Boolean(until && until <= new Date());
};
const formatDay = (date) => date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const Player = ({ src, unplayableText = "This browser can't play this file. Download it to watch." }) => {
  // e.g. an HEVC .mov from an iPhone in Chrome — the file is fine, the browser can't decode it.
  const [unplayable, setUnplayable] = useState(false);
  useEffect(() => setUnplayable(false), [src]);
  return (
    <Box sx={{ bgcolor: "#0B0B0F", borderRadius: "12px", overflow: "hidden" }}>
      {unplayable ? (
        <Typography sx={{ color: "#E5E7EB", fontSize: 13.5, p: 4, textAlign: "center" }}>{unplayableText}</Typography>
      ) : (
        <video
          key={src}
          src={src}
          controls
          playsInline
          preload="metadata"
          onError={() => setUnplayable(true)}
          style={{ display: "block", width: "100%", maxHeight: "60vh", background: "#0B0B0F" }}
        />
      )}
    </Box>
  );
};

const Caption = ({ text }) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>
      Caption
    </Typography>
    {text ? (
      <Typography sx={{ fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        <LinkifiedText text={text} />
      </Typography>
    ) : (
      <Typography sx={{ fontSize: 13, color: "#9CA3AF", fontStyle: "italic" }}>No caption.</Typography>
    )}
  </Box>
);

// `versions` = every version of this item (to date the file, see expiresOn).
export const VideoView = ({ version, versions }) => {
  const until = expiresOn(version.video, versions);
  if (until && until <= new Date()) {
    return (
      <Box>
        <Box sx={{ bgcolor: "#F3F4F6", borderRadius: "12px", p: 4, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>This video was removed after {RETENTION_DAYS} days</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6B7280", mt: 0.5 }}>
            {version.video.fileName} · uploaded {formatDay(new Date(until.getTime() - RETENTION_DAYS * DAY_MS))}
          </Typography>
        </Box>
        <Caption text={version.caption} />
      </Box>
    );
  }
  return (
    <Box>
      <Player src={version.video.url} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1, flexWrap: "wrap" }}>
        <Typography noWrap sx={{ fontSize: 12.5, color: "#6B7280", minWidth: 0 }}>
          {version.video.fileName} · {formatBytes(version.video.size)}
          {until && ` · available until ${formatDay(until)}`}
        </Typography>
        <Button
          size="small"
          component="a"
          href={version.video.url}
          target="_blank"
          rel="noopener noreferrer"
          download
          startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
          sx={{ textTransform: "none", color: "#374151" }}
        >
          Download
        </Button>
      </Box>
      <Caption text={version.caption} />
    </Box>
  );
};

const pickError = (file) => {
  if (!VIDEO_TYPES.includes(mimeOf(file))) return "Only MP4, MOV, WebM or M4V videos can be uploaded.";
  if (file.size > MAX_BYTES) return "Videos can be at most 1GB.";
  return null;
};

// New cut and/or caption. No new file = a caption-only fix on the current cut.
// `onUploadingChange` lets the drawer ask before closing mid-upload.
export const VideoEditor = ({ item, scope, isSaving, onCancel, onSave, onUploadingChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef(null);
  const uploadRef = useRef(null);
  // The finished upload for the current `file` — a failed save (409, network)
  // retries with it instead of pushing the same 500MB again.
  const uploadedRef = useRef(null);
  const [baseVersion, setBaseVersion] = useState(item.currentVersion);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState(item.caption || "");
  const [progress, setProgress] = useState(null);
  const [dragging, setDragging] = useState(false);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const uploading = progress !== null;
  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);
  useEffect(() => () => onUploadingChange?.(false), [onUploadingChange]);
  // Closing the tab mid-upload silently loses a long upload — ask first.
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);
  // Leaving the editor (drawer closed, another creator picked) cancels the upload.
  useEffect(() => () => uploadRef.current?.abort(), []);

  const choose = (picked) => {
    if (!picked) return;
    const error = pickError(picked);
    if (error) return enqueueSnackbar(error, { variant: "error" });
    uploadedRef.current = null;
    setFile(picked);
  };

  // Someone else saved while this was open: the save would 409. Say so, and
  // let the person knowingly put their cut on top of the newer version.
  const staleBy = item.currentVersion !== baseVersion ? item.currentVersion : null;

  // A caption-only fix can't reuse a cut S3 has already deleted.
  const needsVideo = baseVersion === 0 || (item.video && isExpired(item.video, item.versions));
  const captionChanged = caption.trim() !== (item.caption || "").trim();
  const busy = uploading || isSaving;
  const canSave = !busy && !staleBy && (file || (!needsVideo && captionChanged));

  const handleSave = async () => {
    let video = file && uploadedRef.current?.file === file ? uploadedRef.current.video : null;
    if (file && !video) {
      setProgress(0);
      uploadRef.current = uploadToApproval(scope, item.id, file, mimeOf(file), setProgress);
      try {
        video = { url: await uploadRef.current.promise, fileName: file.name };
        uploadedRef.current = { file, video };
      } catch (e) {
        if (e.message !== "Upload cancelled") enqueueSnackbar(errorMessage(e, e.message), { variant: "error" });
        return;
      } finally {
        uploadRef.current = null;
        setProgress(null);
      }
    }
    onSave({ ...(video && { video }), caption: caption.trim(), baseVersion });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={VIDEO_TYPES.join(",")}
        onChange={(e) => {
          choose(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {file ? (
        <Box>
          <Player src={previewUrl} unplayableText="No preview in this browser — the file will still upload fine." />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1 }}>
            <Typography noWrap sx={{ fontSize: 12.5, color: "#6B7280", minWidth: 0 }}>
              {file.name} · {formatBytes(file.size)}
            </Typography>
            {!busy && (
              <Button size="small" onClick={() => { uploadedRef.current = null; setFile(null); }} sx={{ textTransform: "none", color: "#6B7280" }}>
                {needsVideo ? "Choose another" : "Keep current video"}
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            choose(e.dataTransfer.files?.[0]);
          }}
          sx={{
            border: `1.5px dashed ${dragging ? "#FF5200" : "#D1D5DB"}`,
            bgcolor: dragging ? "rgba(255,82,0,0.05)" : "#FAFAFA",
            borderRadius: "12px",
            py: 4,
            px: 2,
            textAlign: "center",
            cursor: "pointer",
            transition: "all .15s",
            "&:hover": { borderColor: "#FF5200" },
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 34, color: "#FF5200" }} />
          <Typography sx={{ fontWeight: 600, fontSize: 14, mt: 0.5 }}>
            {baseVersion === 0
              ? "Choose a video or drop it here"
              : needsVideo
                ? "The current video was removed after 90 days — upload it again"
                : "Replace the video (optional)"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6B7280" }}>MP4, MOV, WebM or M4V · up to 1GB</Typography>
        </Box>
      )}

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Caption"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        disabled={busy}
        inputProps={{ maxLength: CAPTION_MAX }}
        helperText={`The caption you'll post with this video · ${caption.length}/${CAPTION_MAX}`}
        sx={{ "& .MuiInputBase-root": { borderRadius: "12px", fontSize: 14 } }}
      />

      {uploading && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Uploading… keep this page open</Typography>
            <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>{progress}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 6, borderRadius: 3, bgcolor: "#F3F4F6", "& .MuiLinearProgress-bar": { bgcolor: "#FF5200" } }}
          />
        </Box>
      )}

      {staleBy && (
        <Box sx={{ bgcolor: "#FEF3C7", borderRadius: "10px", p: 1.5, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, color: "#92400E", flex: 1, minWidth: 200 }}>
            Someone saved v{staleBy} while you were editing.
          </Typography>
          <Button size="small" onClick={() => setBaseVersion(staleBy)} sx={{ ...secondaryButtonSx, py: 0.25 }}>
            Submit on top of v{staleBy}
          </Button>
        </Box>
      )}
      <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
        Saving creates version {baseVersion + 1}
        {baseVersion > 0 && " and asks the brand to approve again"}.
      </Typography>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={uploading ? () => uploadRef.current?.abort() : onCancel}
          disabled={isSaving}
          variant={uploading ? "outlined" : "text"}
          sx={uploading ? secondaryButtonSx : { textTransform: "none" }}
        >
          {uploading ? "Cancel upload" : "Cancel"}
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={handleSave} sx={primaryButtonSx}>
          {busy ? <CircularProgress size={18} color="inherit" /> : file ? "Upload & submit" : "Save caption"}
        </Button>
      </Box>
    </Box>
  );
};
